import React from 'react';
import PropTypes from 'prop-types';

import Message from '@decisiv/ui-components/lib/components/Message';
import Select from '@decisiv/ui-components/lib/components/Select';
import TextField from '@decisiv/ui-components/lib/components/TextField';

import ExistingUserCard from './ExistingUserCard';
import StepHeader from './StepHeader';
import { DETAILS_STEP } from './copy';
import { POPOVER_Z_INDEX } from './layers';
import {
  FieldGroup,
  PhoneCodeColumn,
  PhoneField,
  PhoneNumberColumn,
  PhoneRow,
  StepBody,
} from './styles';

/**
 * Step 2: who the user is.
 *
 * The two duplicate checks are what this step exists for — the ticket's whole
 * point is preventing a second record for somebody who already exists. A
 * mobile match still just warns and blocks CONTINUE. An email match instead
 * gets classified against Step 1's accounts (see useCreateUserForm's
 * `checks.existingUser`): 'restore' and 'add' show ExistingUserCard here and
 * still block CONTINUE — that card's own button is the only way forward —
 * while 'acknowledge' opens CONTINUE onto Step 3's acknowledgment screen
 * instead. Anything else (no accessible match, or already active on one of
 * the selected accounts) falls back to the plain warning this step always
 * showed.
 *
 * There is no Username field, and no GPS or External Visibility fields: the
 * design collects none of them. The username is derived from the email server
 * side and reported on the success panel.
 *
 * The Authentication field at the bottom is PD-4684's, and it is the one field
 * here whose options the browser cannot work out: which providers a user may sign
 * in with follows from the accounts chosen in step 1 and from whether their email
 * domain carries an SSO rule, both of which only the server knows. See
 * useAuthenticationOptions.
 */
function UserDetailsStep({
  authentication,
  authenticationOptions,
  checks,
  draft,
  isAskable,
  isLoadingAuthentication,
  jobRoleOptions,
  onBlurEmail,
  onBlurMobile,
  onChange,
  onResolveExisting,
  phoneCountryCodeOptions,
  serverMessages,
}) {
  const handle = (field) => (event) => onChange(field, event.target.value);
  const existingUserScenario = checks.existingUser?.scenario;

  // A rejected create beats a blur check: it is the more recent word on the field,
  // and for the mobile it may be a rule the blur check does not cover (a Decisiv
  // outbound number, say). Format is reported before duplication — a malformed
  // number cannot meaningfully be somebody else's.
  const emailWarning =
    serverMessages.email ||
    (checks.emailTaken && !checks.existingUser
      ? DETAILS_STEP.emailTaken
      : undefined);

  const mobileWarning =
    serverMessages.mobile ||
    (!checks.mobileValid && DETAILS_STEP.mobileInvalid) ||
    (checks.mobileTaken ? DETAILS_STEP.mobileTaken : undefined);

  // Nothing to offer, for any of three different reasons, so the field is inert.
  // Opening a select onto an empty list tells the admin nothing.
  //
  // The empty list is the failed-fetch case: the server never answers with one
  // (see copy.authenticationUnavailable), and useAuthenticationOptions empties the
  // offer when the request rejects.
  const authenticationClosed =
    isLoadingAuthentication || !isAskable || authenticationOptions.length === 0;

  // What the Authentication field says about itself. Every state that leaves it
  // unusable explains itself, because a disabled required field with no reason
  // given reads as a fault. Otherwise it is the design's own help line.
  //
  // Order matters: the three unusable states are mutually exclusive in practice but
  // not by construction, and "no email yet" has to beat "the answer never arrived"
  // — there is nothing to have asked for before there is an address to check.
  const authenticationHelp = () => {
    if (isLoadingAuthentication) return DETAILS_STEP.authenticationLoading;
    if (!isAskable) return DETAILS_STEP.authenticationNeedsEmail;

    if (authentication.locked) {
      const [locked] = authenticationOptions;

      return DETAILS_STEP.authenticationLocked(
        locked ? locked.label : authentication.provider,
      );
    }

    if (authenticationOptions.length === 0) {
      return DETAILS_STEP.authenticationUnavailable;
    }

    return DETAILS_STEP.authenticationHelp;
  };

  return (
    <StepBody>
      <StepHeader
        showRequiredKey
        subtitle={DETAILS_STEP.subtitle}
        title={DETAILS_STEP.title}
      />
      {(existingUserScenario === 'restore' ||
        existingUserScenario === 'add') && (
        <ExistingUserCard
          existingUser={checks.existingUser}
          variant={existingUserScenario}
          onAction={() => onResolveExisting()}
        />
      )}
      <FieldGroup>
        <TextField
          required
          label={DETAILS_STEP.firstName}
          name="firstName"
          value={draft.firstName}
          warningMessage={serverMessages.firstName}
          withWarningIntent={Boolean(serverMessages.firstName)}
          onChange={handle('firstName')}
        />
        <TextField
          required
          label={DETAILS_STEP.lastName}
          name="lastName"
          value={draft.lastName}
          warningMessage={serverMessages.lastName}
          withWarningIntent={Boolean(serverMessages.lastName)}
          onChange={handle('lastName')}
        />
        <Select
          helpMessage={DETAILS_STEP.jobRoleHelp}
          label={DETAILS_STEP.jobRole}
          name="jobRole"
          options={jobRoleOptions}
          value={draft.jobRole}
          // Without this the dropdown renders under the wizard — see ./layers.
          zIndex={POPOVER_Z_INDEX}
          onChange={(value) => onChange('jobRole', value)}
        />
      </FieldGroup>
      {/* The mockup splits this step into two Form Sections — who the user is,
          then how to reach them — 20px apart, with 10px inside each. StepBody's
          own gap supplies the 20. */}
      <FieldGroup>
        <TextField
          required
          label={DETAILS_STEP.email}
          name="email"
          type="email"
          value={draft.email}
          warningMessage={emailWarning}
          withWarningIntent={Boolean(emailWarning)}
          onBlur={onBlurEmail}
          onChange={handle('email')}
        />
        <PhoneField>
          <PhoneRow>
            <PhoneCodeColumn>
              <Select
                hideClearButton
                label={DETAILS_STEP.countryCode}
                name="mobileCountryCode"
                options={phoneCountryCodeOptions}
                value={draft.mobileCountryCode}
                // Both halves of the pair go amber together, as the mockup draws
                // them: the code is as likely to be the wrong part as the number.
                // Select spreads its props onto InputField, so this reaches it.
                withWarningIntent={Boolean(mobileWarning)}
                zIndex={POPOVER_Z_INDEX}
                onChange={(value) => onChange('mobileCountryCode', value)}
              />
            </PhoneCodeColumn>
            <PhoneNumberColumn>
              <TextField
                label={DETAILS_STEP.mobile}
                name="mobile"
                type="tel"
                value={draft.mobile}
                withWarningIntent={Boolean(mobileWarning)}
                onBlur={onBlurMobile}
                onChange={handle('mobile')}
              />
            </PhoneNumberColumn>
          </PhoneRow>
          {/* Beneath the whole row rather than inside the number field, so it
              starts at the row's left edge as in the mockup. Message is what
              InputField renders for these itself, so they look the same. */}
          {mobileWarning ? (
            <Message intent="warning">{mobileWarning}</Message>
          ) : (
            <Message intent="help">{DETAILS_STEP.mobileHelp}</Message>
          )}
        </PhoneField>
      </FieldGroup>
      {/* Its own Form Section, a third group below the contact one, as the mockup
          places it (Figma 1153:84744 sits 20px under the mobile row). */}
      <FieldGroup>
        <Select
          required
          // A required field has nothing to clear to: KeyDS offers the clear button
          // whenever a value is selected, and clearing emits an empty string, which
          // would leave the wizard holding a choice the admin cannot see and the
          // server reads as password login. Same reason the country code hides it.
          hideClearButton
          // A domain rule cannot be overridden, which both existing UIs render the
          // same way: a single-option disabled select on the legacy form, a plain
          // non-interactive Tag on the user access page. The value is put there by
          // useAuthenticationOptions, not by the admin.
          disabled={authentication.locked || authenticationClosed}
          helpMessage={
            serverMessages.ssoProvider ? undefined : authenticationHelp()
          }
          label={DETAILS_STEP.authentication}
          name="ssoProvider"
          options={authenticationOptions}
          value={draft.ssoProvider}
          warningMessage={serverMessages.ssoProvider}
          withWarningIntent={Boolean(serverMessages.ssoProvider)}
          // Without this the dropdown renders under the wizard — see ./layers.
          zIndex={POPOVER_Z_INDEX}
          onChange={(value) => onChange('ssoProvider', value)}
        />
      </FieldGroup>
    </StepBody>
  );
}

const optionsShape = PropTypes.arrayOf(
  PropTypes.shape({ id: PropTypes.string, label: PropTypes.string }),
);

UserDetailsStep.propTypes = {
  /** What the server makes of the email and accounts — see useAuthenticationOptions. */
  authentication: PropTypes.shape({
    /** 'domain' when an email domain rule claims the address, else null. */
    assignment: PropTypes.string,
    locked: PropTypes.bool,
    provider: PropTypes.string,
    requireSso: PropTypes.bool,
  }),
  /** Already in KeyDS `{ id, label }` shape. */
  authenticationOptions: optionsShape,
  /** What the server said on blur about the email and the mobile. */
  checks: PropTypes.shape({
    emailTaken: PropTypes.bool,
    existingUser: PropTypes.shape({
      addAccountIds: PropTypes.arrayOf(PropTypes.number),
      email: PropTypes.string,
      fullName: PropTypes.string,
      jobRole: PropTypes.string,
      mobile: PropTypes.string,
      mobileCountryCode: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      restoreAccountIds: PropTypes.arrayOf(PropTypes.number),
      scenario: PropTypes.oneOf(['restore', 'add', 'acknowledge']),
    }),
    mobileTaken: PropTypes.bool,
    mobileValid: PropTypes.bool,
  }),
  draft: PropTypes.shape({
    email: PropTypes.string,
    firstName: PropTypes.string,
    jobRole: PropTypes.string,
    lastName: PropTypes.string,
    mobile: PropTypes.string,
    mobileCountryCode: PropTypes.string,
    ssoProvider: PropTypes.string,
  }).isRequired,
  /** Whether there is yet an email to work out the options from. */
  isAskable: PropTypes.bool,
  isLoadingAuthentication: PropTypes.bool,
  jobRoleOptions: optionsShape,
  onBlurEmail: PropTypes.func.isRequired,
  onBlurMobile: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  onResolveExisting: PropTypes.func.isRequired,
  phoneCountryCodeOptions: optionsShape,
  /** Field messages from a rejected create, keyed by draft field. */
  serverMessages: PropTypes.objectOf(PropTypes.string),
};

UserDetailsStep.defaultProps = {
  authentication: {
    assignment: null,
    locked: false,
    provider: null,
    requireSso: false,
  },
  authenticationOptions: [],
  checks: {
    emailTaken: false,
    existingUser: null,
    mobileTaken: false,
    mobileValid: true,
  },
  isAskable: false,
  isLoadingAuthentication: false,
  jobRoleOptions: [],
  phoneCountryCodeOptions: [],
  serverMessages: {},
};

export default UserDetailsStep;
